import {
  FolderOpen,
  Search,
  ChevronDown,
  Grid3x3,
  List,
  Upload,
  FileText,
  FileSpreadsheet,
  ScanLine,
  Eye,
  Download,
  Share2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface DocumentsScreenProps {
  className?: string;
}

type FileKind = 'pdf' | 'docx' | 'xlsx' | 'scan';

/** A person a document or space is shared with — initials + brand-ish color. */
interface Member {
  initials: string;
  color: string;
}

const PEOPLE: Record<string, Member> = {
  JP: { initials: 'JP', color: '#2D7EFF' },
  KB: { initials: 'KB', color: '#A78BFA' },
  MS: { initials: 'MS', color: '#34D399' },
  TW: { initials: 'TW', color: '#FBBF24' },
  SC: { initials: 'SC', color: '#F87171' },
};

interface DocItem {
  nameKey: string;
  kind: FileKind;
  size: string;
  chunks: number;
  date: string;
  members: Member[];
}

interface SpaceItem {
  nameKey: string;
  count: number;
  color: string;
  members: Member[];
  active?: boolean;
}

const KIND_STYLE: Record<FileKind, { color: string; bg: string; label: string }> = {
  pdf: { color: '#F87171', bg: 'rgba(248,113,113,0.12)', label: 'PDF' },
  docx: { color: '#2D7EFF', bg: 'rgba(45,126,255,0.12)', label: 'DOCX' },
  xlsx: { color: '#34D399', bg: 'rgba(52,211,153,0.12)', label: 'XLSX' },
  scan: { color: '#FBBF24', bg: 'rgba(251,191,36,0.12)', label: 'SCAN' },
};

/** Wissensräume (knowledge spaces) — folders with their own membership. */
const SPACES: SpaceItem[] = [
  { nameKey: 'spaceAll', count: 248, color: '#2D7EFF', members: [PEOPLE.JP, PEOPLE.KB, PEOPLE.MS, PEOPLE.TW, PEOPLE.SC], active: true },
  { nameKey: 'spaceContracts', count: 64, color: '#A78BFA', members: [PEOPLE.JP, PEOPLE.KB, PEOPLE.MS] },
  { nameKey: 'spaceTechnical', count: 91, color: '#34D399', members: [PEOPLE.KB, PEOPLE.TW] },
  { nameKey: 'spaceProposals', count: 38, color: '#FBBF24', members: [PEOPLE.JP, PEOPLE.SC] },
  { nameKey: 'spaceHR', count: 27, color: '#F87171', members: [PEOPLE.MS] },
];

const DOCS: DocItem[] = [
  { nameKey: 'docName1', kind: 'pdf', size: '2,4 MB', chunks: 184, date: '04.06.2026', members: [PEOPLE.KB, PEOPLE.TW] },
  { nameKey: 'docName2', kind: 'docx', size: '486 KB', chunks: 52, date: '02.06.2026', members: [PEOPLE.JP, PEOPLE.KB, PEOPLE.MS] },
  { nameKey: 'docName3', kind: 'xlsx', size: '1,1 MB', chunks: 73, date: '29.05.2026', members: [PEOPLE.TW] },
  { nameKey: 'docName7', kind: 'xlsx', size: '742 KB', chunks: 61, date: '28.05.2026', members: [PEOPLE.JP, PEOPLE.KB, PEOPLE.TW] },
  { nameKey: 'docName4', kind: 'pdf', size: '912 KB', chunks: 38, date: '27.05.2026', members: [PEOPLE.KB] },
  { nameKey: 'docName8', kind: 'scan', size: '4,2 MB', chunks: 88, date: '24.05.2026', members: [PEOPLE.KB, PEOPLE.TW, PEOPLE.MS] },
  { nameKey: 'docName5', kind: 'docx', size: '254 KB', chunks: 21, date: '21.05.2026', members: [PEOPLE.JP, PEOPLE.SC] },
  { nameKey: 'docName6', kind: 'pdf', size: '3,8 MB', chunks: 96, date: '18.05.2026', members: [] },
  { nameKey: 'docName9', kind: 'docx', size: '318 KB', chunks: 29, date: '15.05.2026', members: [PEOPLE.MS] },
];

function FileIcon({ kind }: { kind: FileKind }) {
  const s = KIND_STYLE[kind];
  const Icon = kind === 'xlsx' ? FileSpreadsheet : kind === 'scan' ? ScanLine : FileText;
  return (
    <div className="ds-fileicon" style={{ background: s.bg, color: s.color }} aria-hidden="true">
      <Icon size={22} strokeWidth={2} />
    </div>
  );
}

/** Stacked, overlapping avatar bubbles with a "+N" overflow chip. */
function Avatars({ members, max = 3 }: { members: Member[]; max?: number }) {
  const shown = members.slice(0, max);
  const extra = members.length - shown.length;
  return (
    <div className="ds-avatars" aria-hidden="true">
      {shown.map((m, i) => (
        <span key={`${m.initials}-${i}`} className="ds-avatar" style={{ background: m.color }}>
          {m.initials}
        </span>
      ))}
      {extra > 0 && <span className="ds-avatar ds-avatar--more">+{extra}</span>}
    </div>
  );
}

export function DocumentsScreen({ className }: DocumentsScreenProps) {
  const { t } = useTranslation('demo');
  return (
    <div className={`ds-root${className ? ` ${className}` : ''}`}>
      <style>{css}</style>

      {/* Header */}
      <header className="ds-header">
        <div className="ds-header-title">
          <span className="ds-icon-pill" aria-hidden="true">
            <FolderOpen size={20} color="#2D7EFF" strokeWidth={2.2} />
          </span>
          <div>
            <h1 className="ds-h1">{t('documents.title')}</h1>
            <p className="ds-subtitle">{t('documents.subtitle')}</p>
          </div>
        </div>
      </header>

      {/* Wissensräume — folders with per-space sharing */}
      <section className="ds-spaces" aria-label={t('documents.spacesTitle')}>
        <div className="ds-spaces-head">
          <h2 className="ds-spaces-title">{t('documents.spacesTitle')}</h2>
        </div>
        <ul className="ds-spaces-list">
          {SPACES.map((sp) => (
            <li key={sp.nameKey} className={`ds-space${sp.active ? ' ds-space--active' : ''}`}>
              <span
                className="ds-space-icon"
                style={{ background: `${sp.color}1f`, color: sp.color, borderColor: `${sp.color}40` }}
                aria-hidden="true"
              >
                <FolderOpen size={18} strokeWidth={2.2} />
              </span>
              <div className="ds-space-body">
                <span className="ds-space-name">{t(`documents.${sp.nameKey}`)}</span>
                <span className="ds-space-count">{t('documents.filesCount', { count: sp.count })}</span>
              </div>
              <Avatars members={sp.members} max={3} />
            </li>
          ))}
        </ul>
      </section>

      {/* Toolbar */}
      <div className="ds-toolbar">
        <label className="ds-search">
          <Search size={16} color="#94A3C2" aria-hidden="true" />
          <input
            type="search"
            placeholder={t('documents.searchPlaceholder')}
            aria-label={t('documents.searchAriaLabel')}
          />
        </label>
        <button type="button" className="ds-sort">
          {t('documents.sortNewest')}
          <ChevronDown size={15} aria-hidden="true" />
        </button>
        <div className="ds-viewtoggle" role="group" aria-label={t('documents.viewToggleAriaLabel')}>
          <button type="button" className="ds-view ds-view-active" aria-pressed="true" aria-label={t('documents.gridViewAriaLabel')}>
            <Grid3x3 size={16} aria-hidden="true" />
          </button>
          <button type="button" className="ds-view" aria-pressed="false" aria-label={t('documents.listViewAriaLabel')}>
            <List size={16} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Dropzone */}
      <button type="button" className="ds-dropzone">
        <span className="ds-drop-circle" aria-hidden="true">
          <Upload size={22} color="#2D7EFF" strokeWidth={2.2} />
        </span>
        <span className="ds-drop-main">{t('documents.dropMain')}</span>
        <span className="ds-drop-caption">{t('documents.dropCaption')}</span>
      </button>

      {/* Grid */}
      <ul className="ds-grid">
        {DOCS.map((doc) => {
          const docName = t(`documents.${doc.nameKey}`);
          const shared = doc.members.length > 0;
          return (
            <li key={doc.nameKey} className="ds-card">
              <div className="ds-card-top">
                <FileIcon kind={doc.kind} />
                <span className="ds-status">
                  <span className="ds-dot" aria-hidden="true" />
                  {t('documents.statusProcessed')}
                </span>
              </div>
              <h2 className="ds-name" title={docName}>{docName}</h2>
              <div className="ds-meta">
                <span>{doc.size}</span>
                <span className="ds-meta-sep" aria-hidden="true">·</span>
                <span>{doc.chunks} {t('documents.chunks')}</span>
                <span className="ds-meta-sep" aria-hidden="true">·</span>
                <span>{doc.date}</span>
              </div>
              <div className="ds-card-foot">
                {shared ? (
                  <div className="ds-share">
                    <Avatars members={doc.members} max={3} />
                    <span className="ds-share-label">{t('documents.sharedWith')}</span>
                  </div>
                ) : (
                  <span className="ds-share-label ds-share-label--solo">{t('documents.onlyYou')}</span>
                )}
                <div className="ds-actions">
                  <button type="button" className="ds-act" aria-label={t('documents.viewAction', { name: docName })}>
                    <Eye size={15} aria-hidden="true" />
                  </button>
                  <button type="button" className="ds-act" aria-label={t('documents.downloadAction', { name: docName })}>
                    <Download size={15} aria-hidden="true" />
                  </button>
                  <button type="button" className="ds-act ds-act--share" aria-label={t('documents.shareAction', { name: docName })}>
                    <Share2 size={15} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const css = `
.ds-root { font-family: Inter, system-ui, sans-serif; color: #E2E8F0; padding: 4px 2px 12px; }
.ds-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; }
.ds-header-title { display: flex; align-items: center; gap: 12px; }
.ds-icon-pill { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 11px; background: rgba(45,126,255,0.10); border: 1px solid rgba(45,126,255,0.22); }
.ds-h1 { margin: 0; font-size: 21px; font-weight: 700; color: #E2E8F0; letter-spacing: -0.01em; }
.ds-subtitle { margin: 2px 0 0; font-size: 13px; color: #94A3C2; }

/* ── Wissensräume (folders) ── */
.ds-spaces { margin-bottom: 18px; }
.ds-spaces-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.ds-spaces-title { margin: 0; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #4A5568; }
.ds-spaces-list { list-style: none; margin: 0; padding: 0 0 2px; display: grid; grid-auto-flow: column; grid-auto-columns: minmax(186px, 1fr); gap: 10px; overflow-x: auto; scrollbar-width: none; }
.ds-spaces-list::-webkit-scrollbar { height: 0; }
.ds-space { display: flex; align-items: center; gap: 10px; padding: 11px 12px; border-radius: 12px; background: #141B27; border: 1px solid #1E2D45; min-width: 0; cursor: pointer; transition: border-color .15s, background .15s, transform .15s; }
.ds-space:hover { border-color: #30415F; transform: translateY(-2px); }
.ds-space--active { background: rgba(45,126,255,0.10); border-color: rgba(45,126,255,0.45); }
.ds-space-icon { display: inline-flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 10px; border: 1px solid transparent; flex-shrink: 0; }
.ds-space-body { display: flex; flex-direction: column; min-width: 0; flex: 1 1 auto; }
.ds-space-name { font-size: 13px; font-weight: 600; color: #E2E8F0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ds-space-count { font-size: 11.5px; color: #94A3C2; margin-top: 1px; }

/* ── shared-with avatar bubbles ── */
.ds-avatars { display: inline-flex; align-items: center; flex-shrink: 0; }
.ds-avatar { display: inline-grid; place-items: center; width: 24px; height: 24px; border-radius: 50%; font-size: 9.5px; font-weight: 700; color: #0A0F1C; border: 2px solid #141B27; margin-left: -7px; }
.ds-avatar:first-child { margin-left: 0; }
.ds-avatar--more { background: #30415F; color: #CBD5E1; }

.ds-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; }
.ds-search { display: flex; align-items: center; gap: 8px; flex: 1 1 240px; min-width: 200px; background: #0A0F1C; border: 1px solid #1E2D45; border-radius: 11px; padding: 0 12px; height: 40px; }
.ds-search input { flex: 1; background: transparent; border: none; outline: none; color: #E2E8F0; font-size: 13.5px; font-family: inherit; }
.ds-search input::placeholder { color: #4A5568; }
.ds-search:focus-within { border-color: #30415F; }
.ds-sort { display: flex; align-items: center; gap: 6px; height: 40px; padding: 0 14px; background: #141B27; border: 1px solid #1E2D45; border-radius: 11px; color: #94A3C2; font-size: 13px; font-family: inherit; cursor: pointer; white-space: nowrap; }
.ds-sort:hover { background: rgba(255,255,255,0.04); border-color: #30415F; }
.ds-viewtoggle { display: flex; background: #0A0F1C; border: 1px solid #1E2D45; border-radius: 11px; padding: 3px; gap: 3px; height: 40px; }
.ds-view { display: inline-flex; align-items: center; justify-content: center; width: 34px; border: none; border-radius: 8px; background: transparent; color: #94A3C2; cursor: pointer; }
.ds-view:hover { color: #E2E8F0; }
.ds-view-active { background: #2D7EFF; color: #fff; }
.ds-dropzone { display: flex; flex-direction: column; align-items: center; gap: 6px; width: 100%; padding: 22px 20px; margin-bottom: 22px; background: rgba(255,255,255,0.02); border: 1.5px dashed #30415F; border-radius: 16px; cursor: pointer; font-family: inherit; transition: border-color .15s, background .15s; }
.ds-dropzone:hover { border-color: #2D7EFF; background: rgba(45,126,255,0.05); }
.ds-drop-circle { display: inline-flex; align-items: center; justify-content: center; width: 48px; height: 48px; border-radius: 50%; background: rgba(45,126,255,0.12); border: 1px solid rgba(45,126,255,0.25); margin-bottom: 4px; }
.ds-drop-main { font-size: 14px; font-weight: 600; color: #E2E8F0; }
.ds-drop-caption { font-size: 12px; color: #4A5568; }
.ds-grid { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(238px, 1fr)); gap: 14px; }
.ds-card { display: flex; flex-direction: column; background: #141B27; border: 1px solid #1E2D45; border-radius: 14px; padding: 16px; box-shadow: 0 1px 2px rgba(0,0,0,0.25); transition: border-color .15s, transform .15s; }
.ds-card:hover { border-color: #30415F; transform: translateY(-2px); }
.ds-card-top { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 12px; }
.ds-fileicon { display: inline-flex; align-items: center; justify-content: center; width: 42px; height: 42px; border-radius: 11px; }
.ds-status { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 600; color: #34D399; background: rgba(52,211,153,0.10); border: 1px solid rgba(52,211,153,0.22); border-radius: 999px; padding: 4px 9px; }
.ds-dot { width: 6px; height: 6px; border-radius: 50%; background: #34D399; animation: ds-pulse 2s ease-in-out infinite; }
@keyframes ds-pulse { 0%,100% { opacity: 1; box-shadow: 0 0 0 0 rgba(52,211,153,0.5); } 50% { opacity: .5; box-shadow: 0 0 0 4px rgba(52,211,153,0); } }
.ds-name { margin: 0 0 8px; font-size: 14px; font-weight: 600; color: #E2E8F0; line-height: 1.35; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ds-meta { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #94A3C2; margin-bottom: 13px; }
.ds-meta-sep { color: #4A5568; }
.ds-card-foot { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: auto; padding-top: 12px; border-top: 1px solid #1E2D45; }
.ds-share { display: flex; align-items: center; gap: 8px; min-width: 0; }
.ds-share-label { font-size: 11px; color: #94A3C2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ds-share-label--solo { color: #4A5568; }
.ds-actions { display: flex; gap: 6px; flex-shrink: 0; }
.ds-act { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 9px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: #94A3C2; cursor: pointer; }
.ds-act:hover { background: rgba(45,126,255,0.12); border-color: rgba(45,126,255,0.3); color: #2D7EFF; }
.ds-act--share:hover { background: rgba(124,58,237,0.16); border-color: rgba(124,58,237,0.4); color: #A78BFA; }
@media (max-width: 640px) {
  .ds-root { padding: 2px 0 6px; }
  .ds-header { margin-bottom: 12px; }
  .ds-h1 { font-size: 19px; }
  .ds-spaces { margin-bottom: 12px; }
  .ds-toolbar { margin-bottom: 12px; }
  .ds-dropzone { padding: 16px; margin-bottom: 14px; }
  .ds-drop-circle { width: 42px; height: 42px; margin-bottom: 2px; }
  .ds-grid { gap: 10px; }
  .ds-card { padding: 13px; }
  /* keep the file list focused on phones instead of an endless scroll */
  .ds-grid .ds-card:nth-child(n+6) { display: none; }
}
@media (prefers-reduced-motion: reduce) { .ds-dot { animation: none; } .ds-card, .ds-space { transition: none; } }

/* ── light-mode overrides (status/file-type accents stay) ── */
:root:not(.dark) .ds-root,
:root:not(.dark) .ds-h1,
:root:not(.dark) .ds-space-name,
:root:not(.dark) .ds-name,
:root:not(.dark) .ds-drop-main,
:root:not(.dark) .ds-search input { color: #0f172a; }
:root:not(.dark) .ds-subtitle,
:root:not(.dark) .ds-space-count,
:root:not(.dark) .ds-meta,
:root:not(.dark) .ds-share-label,
:root:not(.dark) .ds-sort,
:root:not(.dark) .ds-view { color: #475569; }
:root:not(.dark) .ds-spaces-title,
:root:not(.dark) .ds-meta-sep,
:root:not(.dark) .ds-share-label--solo,
:root:not(.dark) .ds-drop-caption,
:root:not(.dark) .ds-search input::placeholder { color: #94a3b8; }
:root:not(.dark) .ds-space,
:root:not(.dark) .ds-sort,
:root:not(.dark) .ds-card { background: #f8fafc; border-color: #e2e8f0; }
:root:not(.dark) .ds-search,
:root:not(.dark) .ds-viewtoggle { background: #eef2f7; border-color: #e2e8f0; }
:root:not(.dark) .ds-space:hover,
:root:not(.dark) .ds-card:hover,
:root:not(.dark) .ds-search:focus-within { border-color: #cbd5e1; }
:root:not(.dark) .ds-sort:hover { background: rgba(15,23,42,0.03); border-color: #cbd5e1; }
:root:not(.dark) .ds-view:hover { color: #0f172a; }
:root:not(.dark) .ds-dropzone { background: rgba(15,23,42,0.02); border-color: #cbd5e1; }
:root:not(.dark) .ds-card-foot { border-top-color: #e2e8f0; }
:root:not(.dark) .ds-avatar { border-color: #ffffff; }
:root:not(.dark) .ds-avatar--more { background: #cbd5e1; color: #475569; }
:root:not(.dark) .ds-act { background: rgba(15,23,42,0.04); border-color: rgba(15,23,42,0.08); color: #475569; }
`;
